import React, { useEffect, useState } from "react";
import { API } from "@/App";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { XCircle, Clock, AlertTriangle, Coins, Users, User } from "lucide-react";

const REASON_COLORS = {
  illness: "bg-red-500",
  excused: "bg-blue-500",
  no_show: "bg-orange-500",
  vacation: "bg-emerald-500",
  weather: "bg-cyan-500",
  business: "bg-purple-500",
  trainer: "bg-pink-500",
  other: "bg-slate-500",
  uncategorized: "bg-slate-300",
};

export const CancellationAnalysis = ({ businessId, startStr, endStr }) => {
  const { formatPrice } = useCurrency();
  const fmt = (n) => (formatPrice ? formatPrice(n || 0) : `₺${Number(n || 0).toLocaleString("tr-TR")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!businessId || !startStr || !endStr) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/businesses/${businessId}/reports/cancellation-analysis?start_date=${startStr}&end_date=${endStr}`,
          { credentials: "include" }
        );
        if (res.ok && active) setData(await res.json());
      } catch (e) {
        /* ignore */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [businessId, startStr, endStr]);

  if (loading && !data) {
    return <div className="py-10 text-center text-sm text-slate-500" data-testid="cancel-analysis-loading">Yükleniyor...</div>;
  }
  if (!data) return null;

  const maxCount = Math.max(1, ...data.by_reason.map((r) => r.count));
  const ca = data.commission_awarded || {};

  return (
    <div className="space-y-6" data-testid="cancellation-analysis">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><XCircle className="w-4 h-4" /> Toplam İptal</div>
            <p className="text-2xl font-bold mt-1" data-testid="cancel-total">{data.total}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Clock className="w-4 h-4" /> Geç İptal</div>
            <p className="text-2xl font-bold mt-1 text-orange-600">{data.late_count}</p>
            <p className="text-xs text-slate-400">Normal: {data.normal_count}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Coins className="w-4 h-4" /> Prim Verilen İptal</div>
            <p className="text-2xl font-bold mt-1 text-blue-600" data-testid="cancel-awarded-count">{ca.trainer_count || 0}</p>
            <p className="text-xs text-blue-500">Antrenör: {fmt(ca.trainer_amount)}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs"><Coins className="w-4 h-4" /> Çalışan Primi (İptal)</div>
            <p className="text-2xl font-bold mt-1 text-purple-600">{fmt(ca.employee_amount)}</p>
            <p className="text-xs text-purple-500">{ca.employee_records || 0} kayıt</p>
          </CardContent>
        </Card>
      </div>

      {/* Reason breakdown */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Neden Bazında İptaller</CardTitle>
        </CardHeader>
        <CardContent>
          {data.by_reason.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Seçilen aralıkta iptal yok.</p>
          ) : (
            <div className="space-y-3" data-testid="cancel-by-reason">
              {data.by_reason.map((r) => (
                <div key={r.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{r.label}</span>
                    <span className="text-slate-500">{r.count} • %{r.percentage}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${REASON_COLORS[r.category] || "bg-slate-400"}`}
                      style={{ width: `${(r.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trainer & member breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-pink-500" /> Antrenör Bazında</CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_trainer.length === 0 ? (
              <p className="text-sm text-slate-400 py-2">Veri yok.</p>
            ) : (
              <div className="divide-y" data-testid="cancel-by-trainer">
                {data.by_trainer.map((t) => (
                  <div key={t.trainer_id} className="flex justify-between py-2 text-sm">
                    <span>{t.name}</span>
                    <span className="font-semibold">{t.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-indigo-500" /> Üye Bazında</CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_member.length === 0 ? (
              <p className="text-sm text-slate-400 py-2">Veri yok.</p>
            ) : (
              <div className="divide-y max-h-72 overflow-y-auto" data-testid="cancel-by-member">
                {data.by_member.map((m) => (
                  <div key={m.member_id} className="flex justify-between py-2 text-sm">
                    <span>{m.name}</span>
                    <span className="font-semibold">{m.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CancellationAnalysis;
